/**
 * Rakhi gift box add-on
 * ---------------------------------------------------------------------------
 * Attaches an optional gift box to the cart as its own line item when the
 * shopper ticks the box on a tagged product page.
 *
 * The parent line item's property rides along on the theme's own form
 * serialization, so this file is only responsible for the second line item.
 * Both items go up in a single /cart/add.js request, which means the cart can
 * never end up holding a gift box without its product, or the reverse.
 *
 * theme.cart.addItemFromForm is wrapped rather than replaced so the theme's
 * existing loading, error and cart-drawer handling keeps working untouched.
 */
(function() {
  'use strict';

  if (!window.theme || !theme.cart || typeof theme.cart.addItemFromForm !== 'function') {
    return;
  }

  var selectors = {
    wrapper: '[data-rakhi-giftbox]',
    checkbox: '[data-rakhi-giftbox-checkbox]',
    variant: '[data-rakhi-giftbox-variant]',
    variantChecked: '[data-rakhi-giftbox-variant]:checked',
    price: '[data-rakhi-giftbox-price]',
    config: '[data-rakhi-giftbox-config]',
    quantity: '[name="quantity"]'
  };

  /**
   * Gift box item staged by the submit listener, read once by the wrapped
   * addItemFromForm on the same synchronous event dispatch.
   */
  var pendingGiftBox = null;

  function parseConfig(wrapper) {
    var node = wrapper.querySelector(selectors.config);

    if (!node) {
      return null;
    }

    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function selectedVariant(wrapper, config) {
    if (!config.variants || !config.variants.length) {
      return null;
    }

    // No radio group means the gift box has a single variant.
    var checked = wrapper.querySelector(selectors.variantChecked);

    if (!checked) {
      return config.variants[0];
    }

    for (var i = 0; i < config.variants.length; i++) {
      if (String(config.variants[i].id) === String(checked.value)) {
        return config.variants[i];
      }
    }

    return config.variants[0];
  }

  function giftBoxQuantity(form, config) {
    if (config.quantityMode !== 'match') {
      return 1;
    }

    var input = form.querySelector(selectors.quantity);
    var quantity = input ? parseInt(input.value, 10) : 1;

    return quantity > 0 ? quantity : 1;
  }

  function decode(value) {
    return decodeURIComponent(String(value).replace(/\+/g, ' '));
  }

  /**
   * Turn the theme's url-encoded form payload back into a /cart/add.js item so
   * it can travel in the same items[] array as the gift box.
   */
  function serializedToItem(data) {
    if (typeof data !== 'string' || !data) {
      return null;
    }

    var item = { id: null, quantity: 1, properties: {} };
    var pairs = data.split('&');

    for (var i = 0; i < pairs.length; i++) {
      if (!pairs[i]) {
        continue;
      }

      var parts = pairs[i].split('=');
      var key = decode(parts[0]);
      var value = decode(parts.slice(1).join('='));

      if (key === 'id') {
        item.id = value;
      } else if (key === 'quantity') {
        item.quantity = parseInt(value, 10) || 1;
      } else if (key.indexOf('properties[') === 0 && key.charAt(key.length - 1) === ']') {
        if (value !== '') {
          item.properties[key.slice('properties['.length, -1)] = value;
        }
      }
    }

    return item.id ? item : null;
  }

  function syncWrapper(wrapper) {
    var config = parseConfig(wrapper);

    if (!config) {
      return;
    }

    var variant = selectedVariant(wrapper, config);

    if (!variant) {
      return;
    }

    var checkbox = wrapper.querySelector(selectors.checkbox);
    var price = wrapper.querySelector(selectors.price);

    if (checkbox) {
      checkbox.value = variant.name;
    }

    if (price) {
      price.textContent = variant.price;
    }
  }

  /**
   * Delegated so gift box controls rendered after load - quick view, for
   * instance - work without re-initialising. Liquid already paints the correct
   * initial state, so there is nothing to set up on page load.
   */
  document.addEventListener('change', function(evt) {
    var target = evt.target;

    if (!target || typeof target.closest !== 'function') {
      return;
    }

    var isCheckbox = target.matches(selectors.checkbox);
    var isVariant = target.matches(selectors.variant);

    if (!isCheckbox && !isVariant) {
      return;
    }

    var wrapper = target.closest(selectors.wrapper);

    if (!wrapper) {
      return;
    }

    // Choosing a design only ever means the shopper wants the box, so opt them
    // in rather than making them tick a second control.
    if (isVariant) {
      var checkbox = wrapper.querySelector(selectors.checkbox);

      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
      }
    }

    syncWrapper(wrapper);
  });

  /**
   * Capture phase runs ahead of the theme's own bubble-phase submit handler, so
   * the gift box is staged before addItemFromForm is called.
   */
  document.addEventListener('submit', function(evt) {
    pendingGiftBox = null;

    var form = evt.target;

    if (!form || typeof form.querySelector !== 'function') {
      return;
    }

    var wrapper = form.querySelector(selectors.wrapper);

    if (!wrapper) {
      return;
    }

    var checkbox = wrapper.querySelector(selectors.checkbox);

    if (!checkbox || !checkbox.checked) {
      return;
    }

    var config = parseConfig(wrapper);

    if (!config) {
      return;
    }

    var variant = selectedVariant(wrapper, config);

    if (!variant) {
      return;
    }

    var properties = {};

    if (config.pairProperty && config.parentTitle) {
      properties[config.pairProperty] = config.parentTitle;
    }

    pendingGiftBox = {
      id: variant.id,
      quantity: giftBoxQuantity(form, config),
      properties: properties
    };
  }, true);

  var originalAddItemFromForm = theme.cart.addItemFromForm;

  theme.cart.addItemFromForm = function(data) {
    var giftBox = pendingGiftBox;

    // Read once. A staged box must never leak into an unrelated add.
    pendingGiftBox = null;

    if (!giftBox) {
      return originalAddItemFromForm.apply(theme.cart, arguments);
    }

    var parentItem = serializedToItem(data);

    if (!parentItem) {
      return originalAddItemFromForm.apply(theme.cart, arguments);
    }

    return $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      data: JSON.stringify({ items: [parentItem, giftBox] }),
      contentType: 'application/json',
      dataType: 'json'
    });
  };
})();
